import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Space, 
  Button, 
  message, 
  Spin,
  Divider,
  Tag,
  List,
  Input,
  Alert
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SendOutlined,
  ClockCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { legislativeNoticeService } from '../../services/legislativeNoticeService';
import { attachmentService } from '../../services/attachmentService';
import { commentService } from '../../services/commentService';
import { useAuth } from '../../contexts/AuthContext';
import { LegislativeNotice, Attachment, Comment as CommentType, LegislativeNoticeCategory } from '../../types';
import { formatLocalizedDateTime } from '../../utils/dateUtils';
import { tableStyles as generalTableStyles } from '../../utils/tableUtils';
import '../../styles/contentStyles.css';
import { useTranslation } from 'react-i18next';
import { removeContentEditable, setupDOMObserver, executeWithDelay } from '../../utils/domUtils';

const { Title } = Typography;
const { TextArea } = Input;

// 상세보기용 CSS 스타일 - 편집 버튼 숨김 및 표 스타일 적용
const detailStyles = `
  .preview-content .add-row-btn,
  .preview-content .delete-row-btn,
  .preview-content .table-controls {
    display: none !important;
  }
  
  .preview-content table td[contenteditable="true"] {
    cursor: default;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }
  
  .preview-content table td[contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  .preview-content table td[contenteditable="true"]:hover {
    background-color: transparent;
  }
  
  /* 상세보기에서 모든 편집 가능 요소 비활성화 */
  .preview-content [contenteditable="true"] {
    cursor: default;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }
  
  .preview-content [contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  /* 중첩된 Card 스타일 제거 */
  .preview-content .ant-card {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  
  .preview-content .ant-card-body {
    padding: 0 !important;
    background: transparent !important;
  }
  
  /* 상세보기용 스타일 클래스 적용 */
  .preview-content .title-style,
  .preview-content .pub-title-style {
    font-size: 24px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 20px 0 16px 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
  }
  
  .preview-content .jang-style,
  .preview-content .pub-jang-style {
    font-size: 18px !important;
    font-weight: bold !important;
    text-align: left !important;
    padding: 40px 0 0 80px !important;
    margin: 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
    border: none !important;
    background: none !important;
    outline: none !important;
  }
  
  .preview-content .jo-style,
  .preview-content .pub-jo-style {
    padding: 16px 0 0px 40px !important;
    text-indent: -40px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .hang-style,
  .preview-content .pub-hang-style {
    padding: 0px 0 0px 60px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .ho-style,
  .preview-content .pub-ho-style {
    padding: 0px 0 0px 80px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  
  ${generalTableStyles}
`;

const LegislativeNoticeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, user, isAuthenticated } = useAuth();
  const [legislativeNotice, setLegislativeNotice] = useState<LegislativeNotice | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<CommentType[]>([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const { t, i18n } = useTranslation();

  // 카테고리 번역 함수
  const getCategoryTranslation = (category: LegislativeNoticeCategory): string => {
    // 한글 카테고리 값을 영어 키로 매핑
    const getCategoryKey = (cat: string): string => {
      switch (cat) {
        case '교규': return 'KYOGYU';
        case '교령': return 'KYORYUNG';
        case '예규': return 'YEGYU';
        default: return cat;
      }
    };
    
    const categoryKey = getCategoryKey(category);
    return t(`legislativeNotices.categories.${categoryKey}`);
  };

  const fetchComments = useCallback(async () => {
    if (!id || id === 'new') return;
    
    setCommentLoading(true);
    try {
      const data = await commentService.getLegislativeNoticeComments(id);
      setComments(data);
    } catch (error) {
    } finally {
      setCommentLoading(false);
    }
  }, [id]);

  const fetchLegislativeNoticeDetail = useCallback(async () => {
    if (!id || id === 'new') return;
    
    setLoading(true);
    try {
      const data = await legislativeNoticeService.getLegislativeNotice(id);
      setLegislativeNotice(data);
      
      // 마감일 상태 확인
      const deadlineStatus = await legislativeNoticeService.getDeadlineStatus(id);
      setIsDeadlinePassed(deadlineStatus.isDeadlinePassed);
      
      // 첨부파일 별도 조회 (에디터 이미지 제외)
      try {
        const attachmentData = await attachmentService.getLegislativeNoticeAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {
        // 첨부파일 조회 실패 시 빈 배열로 설정
        setAttachments([]);
      }
      
      // 댓글 조회
      await fetchComments();
    } catch (error: any) {
      message.error(t('legislativeNotices.DETAIL_SECTION.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t, fetchComments]);

  useEffect(() => {
    if (id) {
      fetchLegislativeNoticeDetail();
    }
  }, [id, fetchLegislativeNoticeDetail]);

  // 상세보기에서 contenteditable 속성 제거
  useEffect(() => {
    if (legislativeNotice) {
      // 즉시 실행
      removeContentEditable();
      
      // 지연 실행
      const cleanup = executeWithDelay(removeContentEditable);
      
      // MutationObserver 설정
      const previewContent = document.querySelector('.preview-content');
      const observer = previewContent ? setupDOMObserver(previewContent, removeContentEditable, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['contenteditable']
      }) : null;

      return () => {
        cleanup();
        observer?.disconnect();
      };
    }
  }, [legislativeNotice]);

  const handleDelete = async () => {
    if (!legislativeNotice) return;
    
    try {
      await legislativeNoticeService.deleteLegislativeNotice(legislativeNotice.legislative_notice_id);
      message.success(t('legislativeNotices.DETAIL_SECTION.deleteSuccess'));
      navigate('/legislative-notices');
    } catch (error: any) {
      message.error(error.response?.data?.message || t('legislativeNotices.DETAIL_SECTION.deleteFailed'));
    }
  };

  const handleApprove = async () => {
    if (!legislativeNotice) return;
    
    try {
      await legislativeNoticeService.approveLegislativeNotice(legislativeNotice.legislative_notice_id);
      message.success(t('legislativeNotices.DETAIL_SECTION.approveSuccess'));
      fetchLegislativeNoticeDetail();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('legislativeNotices.DETAIL_SECTION.approveFailed'));
    }
  };

  const handleUnapprove = async () => {
    if (!legislativeNotice) return;
    
    try {
      await legislativeNoticeService.unapproveLegislativeNotice(legislativeNotice.legislative_notice_id);
      message.success(t('legislativeNotices.DETAIL_SECTION.unapproveSuccess'));
      fetchLegislativeNoticeDetail();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('legislativeNotices.DETAIL_SECTION.unapproveFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const handleSubmitComment = async () => {
    if (!id || id === 'new' || !commentText.trim()) return;
    
    setSubmitting(true);
    try {
      await commentService.createComment({
        legislativeNoticeId: id,
        content: commentText.trim()
      });
      setCommentText('');
      message.success('댓글이 작성되었습니다.');
      await fetchComments();
    } catch (error: any) {
      message.error(error.response?.data?.message || '댓글 작성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editText.trim()) return;
    
    try {
      await commentService.updateComment(commentId, { content: editText.trim() });
      setEditingCommentId(null);
      setEditText('');
      message.success('댓글이 수정되었습니다.');
      await fetchComments();
    } catch (error: any) {
      message.error('댓글 수정에 실패했습니다.');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await commentService.deleteComment(commentId);
      message.success('댓글이 삭제되었습니다.');
      await fetchComments();
    } catch (error: any) {
      message.error('댓글 삭제에 실패했습니다.');
    }
  };

  const getCategoryColor = (category: LegislativeNoticeCategory) => {
    switch (category) {
      case LegislativeNoticeCategory.KYOGYU:
        return 'blue';
      case LegislativeNoticeCategory.KYORYUNG:
        return 'purple';
      case LegislativeNoticeCategory.YEGYU:
        return 'cyan';
      default:
        return 'default';
    }
  };

  const getDeadlineStatus = (deadlineDate: string) => {
    // 서버 시간대(UTC) 기준으로 계산
    const deadline = new Date(deadlineDate + 'T00:00:00.000Z');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    today.setHours(0, 0, 0, 0);
    
    const isExpired = today > deadline;
    
    if (isExpired) {
      return { color: 'red', text: t('legislativeNotices.deadlineStatus.expired'), icon: <ClockCircleOutlined /> };
    } else {
      const diffTime = deadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 3) {
        return { color: 'orange', text: t('legislativeNotices.deadlineStatus.urgent', { count: diffDays }), icon: <ClockCircleOutlined /> };
      } else {
        return { color: 'green', text: t('legislativeNotices.deadlineStatus.normal', { count: diffDays }), icon: <ClockCircleOutlined /> };
      }
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('legislativeNotices.DETAIL_SECTION.loading')}</div>
      </div>
    );
  }

  if (!legislativeNotice) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('legislativeNotices.DETAIL_SECTION.notFound')}</Title>
        <Button type="primary" onClick={() => navigate('/legislative-notices')}>
          {t('legislativeNotices.DETAIL_SECTION.backToList')}
        </Button>
      </div>
    );
  }

  const deadlineStatus = getDeadlineStatus(legislativeNotice.deadline_date);

  return (
    <div>
      <style>{detailStyles}</style>
      <Card>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/legislative-notices')}
            style={{ marginRight: 16 }}
          >
            {t('legislativeNotices.DETAIL_SECTION.backToList')}
          </Button>
          
          <Space>
            {isAdmin && (
              <>
                {legislativeNotice.is_approved ? (
                  <Button
                    danger
                    onClick={() => {
                      if (window.confirm(t('legislativeNotices.DETAIL_SECTION.unapproveConfirm'))) {
                        handleUnapprove();
                      }
                    }}
                  >
                    {t('legislativeNotices.DETAIL_SECTION.unapprove')}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      if (window.confirm(t('legislativeNotices.DETAIL_SECTION.approveConfirm'))) {
                        handleApprove();
                      }
                    }}
                  >
                    {t('legislativeNotices.DETAIL_SECTION.approve')}
                  </Button>
                )}
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/legislative-notices/${legislativeNotice.legislative_notice_id}/edit`)}
                  disabled={legislativeNotice.is_approved}
                  title={legislativeNotice.is_approved ? t('legislativeNotices.DETAIL_SECTION.approvedCannotEdit') : t('legislativeNotices.DETAIL_SECTION.edit')}
                >
                  {t('legislativeNotices.DETAIL_SECTION.edit')}
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (legislativeNotice.is_approved) {
                      message.warning(t('legislativeNotices.DETAIL_SECTION.approvedCannotDelete'));
                      return;
                    }
                    if (window.confirm(t('legislativeNotices.DETAIL_SECTION.deleteConfirm'))) {
                      handleDelete();
                    }
                  }}
                  disabled={legislativeNotice.is_approved}
                  title={legislativeNotice.is_approved ? t('legislativeNotices.DETAIL_SECTION.approvedCannotDelete') : t('legislativeNotices.DETAIL_SECTION.delete')}
                >
                  {t('legislativeNotices.DETAIL_SECTION.delete')}
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* 제목 및 메타 정보 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>{legislativeNotice.title}</Title>
          <Space size="large" style={{ marginBottom: 16 }}>
            <Tag color={getCategoryColor(legislativeNotice.category)}>
              {getCategoryTranslation(legislativeNotice.category)}
            </Tag>
            <span>{t('legislativeNotices.DETAIL_SECTION.createdAt')}: {formatLocalizedDateTime(legislativeNotice.created_at, i18n.language)}</span>
            {legislativeNotice.updated_at && legislativeNotice.updated_at !== legislativeNotice.created_at && (
              <span>{t('legislativeNotices.DETAIL_SECTION.updatedAt')}: {formatLocalizedDateTime(legislativeNotice.updated_at, i18n.language)}</span>
            )}
            <span>{t('legislativeNotices.DETAIL_SECTION.viewCount')}: {legislativeNotice.view_count.toLocaleString()}</span>
          </Space>
        </div>

        <Divider />

        {/* 마감일 정보 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>{t('legislativeNotices.deadlineDate')}</Title>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontWeight: '500', minWidth: '80px' }}>{t('legislativeNotices.deadlineDate')}:</span>
                <span style={{ fontSize: '16px' }}>
                  {(() => {
                    // UTC 기준으로 날짜 생성하여 시간대 문제 해결
                    const [year, month, day] = legislativeNotice.deadline_date.split('-');
                    const utcDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
                    return utcDate.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long',
                      timeZone: 'UTC'
                    });
                  })()}
                </span>
                <Tag color={deadlineStatus.color} icon={deadlineStatus.icon} style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {deadlineStatus.text}
                </Tag>
              </div>
              {isDeadlinePassed && (
                <Alert
                  message={t('legislativeNotices.DETAIL_SECTION.deadlinePassedMessage')}
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        </div>

        {/* 첨부파일 */}
        {attachments && attachments.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>{t('legislativeNotices.DETAIL_SECTION.attachments')}</Title>
            <Card>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.attachment_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>📎</span>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {decodeURIComponent(attachment.file_name)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {attachmentService.formatFileSize(attachment.file_size)} • {formatLocalizedDateTime(attachment.uploaded_at, i18n.language)}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      {t('legislativeNotices.DETAIL_SECTION.download')}
                    </Button>
                  </div>
                ))}
              </Space>
            </Card>
          </div>
        )}

        {/* 내용 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>{t('legislativeNotices.DETAIL_SECTION.content')}</Title>
          <div 
            className="preview-content"
            style={{ 
              backgroundColor: '#fafafa',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '16px'
            }}
            dangerouslySetInnerHTML={{ __html: legislativeNotice.content }}
          />
        </div>

        {/* 댓글 섹션 */}
        <div>
          <Title level={4}>{t('legislativeNotices.DETAIL_SECTION.comments')}</Title>
          
          {isDeadlinePassed && (
            <Alert
              message={t('legislativeNotices.DETAIL_SECTION.deadlinePassedMessage')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          {!isDeadlinePassed && (
            <Card style={{ marginBottom: 16 }}>
              {isAuthenticated ? (
                <>
                  <TextArea
                    rows={4}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t('legislativeNotices.DETAIL_SECTION.commentPlaceholder')}
                    disabled={submitting}
                    style={{ marginBottom: 16 }}
                  />
                  <Button
                    loading={submitting}
                    onClick={handleSubmitComment}
                    type="primary"
                    icon={<SendOutlined />}
                    disabled={!commentText.trim()}
                  >
                    {t('legislativeNotices.DETAIL_SECTION.commentSubmit')}
                  </Button>
                </>
              ) : (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '8px',
                  border: '1px dashed #d9d9d9'
                }}>
                  <MessageOutlined style={{ fontSize: '24px', color: '#999', marginBottom: '8px' }} />
                  <div style={{ color: '#666', marginBottom: '12px' }}>
                    {t('comments.loginRequired')}
                  </div>
                  <Button 
                    type="primary" 
                    onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
                  >
                    {t('auth.login')}
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* 댓글 목록 */}
          <Title level={4}>{t('legislativeNotices.DETAIL_SECTION.comments')} ({comments.length}{t('common.total')})</Title>
          <List
            loading={commentLoading}
            dataSource={comments}
            itemLayout="horizontal"
            renderItem={(comment) => (
              <List.Item
                actions={
                  comment.user.user_id === user?.user_id || isAdmin
                    ? [
                        editingCommentId === comment.comment_id ? (
                          <Button
                            key="save"
                            size="small"
                            onClick={() => handleEditComment(comment.comment_id)}
                          >
                            {t('legislativeNotices.DETAIL_SECTION.commentSave')}
                          </Button>
                        ) : (
                          <Button
                            key="edit"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setEditingCommentId(comment.comment_id);
                              setEditText(comment.content);
                            }}
                          >
                            {t('legislativeNotices.DETAIL_SECTION.commentEdit')}
                          </Button>
                        ),
                        <Button
                          key="delete"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteComment(comment.comment_id)}
                        >
                          {t('legislativeNotices.DETAIL_SECTION.commentDelete')}
                        </Button>
                      ]
                    : []
                }
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <span>
                        {(() => {
                          const bubmyeng = comment.user?.bubmyeng;
                          const organName = comment.user?.organ_name;
                          const username = comment.user?.role === 'ADMIN' ? 'admin' : (comment.user?.username || '익명');
                          
                          if (bubmyeng && organName) {
                            return `${bubmyeng}(${organName})`;
                          } else if (bubmyeng) {
                            return bubmyeng;
                          } else if (organName) {
                            return `익명(${organName})`;
                          } else {
                            return username;
                          }
                        })()}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {formatLocalizedDateTime(comment.created_at, i18n.language)}
                        {comment.updated_at !== comment.created_at && (
                          <span style={{ marginLeft: '8px' }}>({t('legislativeNotices.DETAIL_SECTION.commentEdited')})</span>
                        )}
                      </span>
                    </Space>
                  }
                  description={
                    editingCommentId === comment.comment_id ? (
                      <div>
                        <TextArea
                          rows={3}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          style={{ marginBottom: 8 }}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingCommentId(null);
                            setEditText('');
                          }}
                        >
                          {t('legislativeNotices.DETAIL_SECTION.commentCancel')}
                        </Button>
                      </div>
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</div>
                    )
                  }
                />
              </List.Item>
            )}
          />
        </div>
      </Card>
    </div>
  );
};

export default LegislativeNoticeDetail; 